import Job from 'jobs/Job'
import { action, observable, makeObservable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { FindUserTeamMembersByUserPublicId } from 'iam/TeamMemberStore'
import TeamMember from 'iam/models/TeamMember'
import { BehaviorSubject } from 'rxjs'
import { CancellationToken } from 'infra/cancellation'

export default class ClientTeamInputEditorViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Client TeamMember(TeamMember property contains the user's team(s)).
   */
  @observable
  clientTeamMember?: TeamMember = undefined

  @observable
  jobs: Job[] = []

  constructor(
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
  ) {
    makeObservable(this)
  }

  /**
   * Validation context for client team.
   */
  validationClientTeamMember = validationContext(this, {
    clientTeamMember: [required('Client team is required')],
  })

  /**
   * Sets client TeamMember to transfer.
   */
  @action.bound
  setClientTeamMember(clientTeamMember?: TeamMember) {
    this.clientTeamMember = clientTeamMember
  }

  /**
   * Indicates if client TeamMember is valid.
   */
  @action.bound
  validateClientTeamMember() {
    return this.validationClientTeamMember.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validationClientTeamMember.reset()
    this.clientTeamMember = undefined
  }

  activate(jobs: Job[]) {
    this._active.next(true)
    this.jobs = jobs
  }

  deactivate() {
    this._active.next(false)
  }

  /**
   * Fetches pros.
   */
  fetchClientTeamMembers = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<TeamMember>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const lowerSearchText = searchText.toLowerCase()
    const ownersPublicId = new Set(this.jobs.map((j) => j.owner.user.publicId))

    // /**
    //  * In this case, we're able to obtain the user's team memberships
    //  * from the TeamMember property.
    //  */
    const result = await Promise.all(
      Array.from(ownersPublicId).map((ownerId) => {
        return this.findTeamMembersByUserPublicId(
          {
            after: params.after,
            limit: params.limit,
            workspace_id: Id.assert(this.jobs[0].workspace.id),
            user_public_id: Id.assert(ownerId),
            include_team: true,
          },
          cancellationToken,
        )
      }),
    )

    const paged = CursorPaged.fromArray(
      result.reduce((acc, curr) => {
        return acc.filter((item) =>
          curr.some((t) => t.team.id === item.team.id),
        )
      }),
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((t) => {
        const teamInfo = `${t.team.name} ${t.team.id}`
        const invalidTeam = collaborators.some((c) =>
          this.jobs.length === 1
            ? c.team === t.team && c.role === 'CHAMPION' && c.type === 'CLIENT'
            : false,
        )
        return !invalidTeam && teamInfo.toLowerCase().includes(lowerSearchText)
      }),
    )
  }
}
